import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/documentation/documentation/node_modules/gatsby-theme-doctornpm/src/components/layout.js";
import shared from '../../../src/shared.js';
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const Note = makeShortcode("Note");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <p>{`To share your code with a limited set of users or teams, you can publish private user-scoped or organization-scoped packages to the npm registry.`}</p>
    <p>{`For more information on scopes and private packages, see "`}<a parentName="p" {...{
        "href": "about-scopes"
      }}>{`About scopes`}</a>{`" and "`}<a parentName="p" {...{
        "href": "about-private-packages"
      }}>{`About private packages`}</a>{`".`}</p>
    <Note mdxType="Note">
      <p><strong parentName="p">{`Note:`}</strong>{` Before you can publish private user-scoped npm packages, you must `}<a href="https://npmjs.com/signup">{`sign up`}</a>{` for a paid npm user account.`}</p>
      <p>{`Additionally, to publish private organization-scoped packages, you must `}<a href="https://npmjs.com/signup">{`create an npm user account`}</a>{`, then `}<a href="https://www.npmjs.com/signup?next=/org/create">{`
create a paid npm organization`}</a>{`.`}</p>
    </Note>
    <h2>{`Creating a private package`}</h2>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`If you are using npmrc to `}<a parentName="p" {...{
            "href": "configuring-your-registry-settings-as-an-npm-enterprise-user#using-npmrc-to-manage-multiple-profiles-for-different-registries"
          }}>{`manage accounts on multiple registries`}</a>{`, on the command line, switch to the appropriate profile:`}</p>
        <pre parentName="li"><code parentName="pre" {...{}}>{`npmrc <profile-name>
`}</code></pre>
      </li>
      <li parentName="ol">
        <p parentName="li">{`On the command line, create a directory for your package:`}</p>
        <pre parentName="li"><code parentName="pre" {...{}}>{`mkdir my-test-package
`}</code></pre>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Navigate to the root directory of your package:`}</p>
        <pre parentName="li"><code parentName="pre" {...{}}>{`cd my-test-package
`}</code></pre>
      </li>
      <li parentName="ol">
        <p parentName="li">{`If you are using git to manage your package code, in the package root directory, run the following commands, replacing `}<inlineCode parentName="p">{`git-remote-url`}</inlineCode>{` with the git remote URL for your package:`}</p>
        <pre parentName="li"><code parentName="pre" {...{}}>{`git init
git remote add origin git://git-remote-url
`}</code></pre>
      </li>
      <li parentName="ol">
        <p parentName="li">{`In the package root directory, run the `}<inlineCode parentName="p">{`npm init`}</inlineCode>{` command and pass the scope to the `}<inlineCode parentName="p">{`scope`}</inlineCode>{` flag:`}</p>
        <ul parentName="li">
          <li parentName="ul">
            <p parentName="li">{`For an organization-scoped package, replace `}<inlineCode parentName="p">{`my-org`}</inlineCode>{` with the name of your organization:`}</p>
            <pre parentName="li"><code parentName="pre" {...{}}>{`npm init --scope=@my-org
`}</code></pre>
          </li>
          <li parentName="ul">
            <p parentName="li">{`For a user-scoped package, replace `}<inlineCode parentName="p">{`my-username`}</inlineCode>{` with your username:`}</p>
            <pre parentName="li"><code parentName="pre" {...{}}>{`npm init --scope=@my-username
`}</code></pre>
          </li>
        </ul>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Respond to the prompts to generate a `}<a href="https://docs.npmjs.com/about-package-json-and-package-lock-json-files"><inlineCode parentName="p">{`package.json`}</inlineCode></a>{` file. For help naming your package, see "`}<a parentName="p" {...{
            "href": "package-name-guidelines"
          }}>{`Package name guidelines`}</a>{`".`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Create a `}<a parentName="p" {...{
            "href": "about-package-readme-files"
          }}>{`README file`}</a>{` that explains what your package code is and how to use it.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`In your preferred text editor, write the code for your package.`}</p>
      </li>
    </ol>
    <h2>{`Reviewing package contents for sensitive or unnecessary information`}</h2>
    <p>{`Publishing sensitive information to the registry can harm your users, compromise your development infrastructure, be expensive to fix, and put you at risk of legal action. `}<strong parentName="p">{`We strongly recommend removing sensitive information, such as private keys, passwords, `}{`[personally identifiable information][pii]`}{` (PII), and credit card data before publishing your package to the registry.`}</strong>{` Even if your package is private, sensitive information can be exposed if the package is made public or downloaded to a computer that can be accessed by more users than intended.`}</p>
    <p>{`For less sensitive information, such as testing data, use a `}<inlineCode parentName="p">{`.npmignore`}</inlineCode>{` or `}<inlineCode parentName="p">{`.gitignore`}</inlineCode>{` file to prevent publishing to the registry. For more information, see `}<a parentName="p" {...{
        "href": "/misc/developers#keeping-files-out-of-your-package"
      }}>{`this article`}</a>{`.`}</p>
    <h2>{`Testing your package`}</h2>
    <p>{`To reduce the chances of publishing bugs, we recommend testing your package before publishing it to the npm registry. To test your package, run `}<inlineCode parentName="p">{`npm install`}</inlineCode>{` with the full path to your package directory:`}</p>
    <pre><code parentName="pre" {...{}}>{`npm install my-package
`}</code></pre>
    <h2>{`Publishing private packages`}</h2>
    <p>{`By default, scoped packages are published with private visibility.`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`On the command line, navigate to the root directory of your package.`}</p>
        <pre parentName="li"><code parentName="pre" {...{}}>{`cd /path/to/package
`}</code></pre>
      </li>
      <li parentName="ol">
        <p parentName="li">{`To publish your private package to the npm registry, run:`}</p>
        <pre parentName="li"><code parentName="pre" {...{}}>{`npm publish
`}</code></pre>
      </li>
      <li parentName="ol">
        <p parentName="li">{`To see your private package page, visit `}<a parentName="p" {...{
            "href": "https://npmjs.com/package/*package-name"
          }}>{`https://npmjs.com/package/*package-name`}</a><em parentName="p">{`, replacing `}</em>{`package-name* with the name of your package. Private packages will say `}<inlineCode parentName="p">{`private`}</inlineCode>{` below the package name on the npm website.`}</p>
        <>{shared['organization-package-private'].image}</>
      </li>
    </ol>
    <p>{`For more information on the `}<inlineCode parentName="p">{`publish`}</inlineCode>{` command, see the `}<a parentName="p" {...{
        "href": "/cli/publish"
      }}>{`CLI documentation`}</a>{`.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      